import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppKey } from "App/Components/ABAC/AbacConstants";
import {
  abacAppsControllerPath,
  abacAttributesControllerPath,
  abacDeleteRequest,
  abacFeatureAttributesControllerPath,
  abacFeatureClientAttributeControllerPath,
  abacFeaturesControllerPath,
  abacGetRequest,
  abacPermissionControllerPath,
  abacPostRequest,
  abacPutRequest,
  abacUserClientAttributeControllerPath,
  abacUserControllerPath,
} from "App/Helpers/queryHelpers";
import { useNotification } from "App/State/Hooks/useNotificationHooks";
import { selectClientValue } from "App/State/Slices/SelectedClientSlice";
import { selectUserInformation } from "App/State/Slices/UserInformationSlice";
import { useAppSelector } from "App/State/Store";
import { IApp, ICreateApp, IEditApp } from "App/Typings/AbacApp";
import { IAttribute, ICreateAttribute, IEditAttribute } from "App/Typings/AbacAttribute";
import { ICreateFeature, IEditFeature, IFeature, IFeatureDto } from "App/Typings/AbacFeature";
import { IFeatureAttribute } from "App/Typings/AbacFeatureAttribute";
import { IClientFeaturesDto, ISyncClientFeature } from "App/Typings/AbacFeatureClientAttribute";
import { IAbacUser, ICreateAbacUser, IEditAbacUser } from "App/Typings/AbacUser";
import { ICreateUserClientAttribute, IUserClientAttribute } from "App/Typings/AbacUserClientAttribute";
import { AxiosError, AxiosResponse } from "axios";

// See React Query v4 docs for more info on useQuery
// https://tanstack.com/query/v4/docs/quick-start

/**
 * @description Gets all ABAC Applications
 * @returns UseQueryResult<IApp[]>
 */
export function useGetAbacAppsQuery() {
  return useQuery<IApp[]>(
    ["abacApps"],
    async () => {
      const response: AxiosResponse<IApp[]> = await abacGetRequest(abacAppsControllerPath + "GetAllApps");

      return response.data.map(convertDates);
    },
    { refetchOnWindowFocus: false }
  );
}

/**
 * @description Gets an ABAC Application with the given ID
 * @param appId The ID for the app you wish to retrieve
 * @returns UseQueryResult<IApp>
 */
export function useGetAbacAppQuery(appId: string) {
  return useQuery<IApp>(["abacApp", { id: appId }], async () => {
    const response: AxiosResponse<IApp> = await abacGetRequest(abacAppsControllerPath + "GetApp/" + encodeURIComponent(appId));

    return convertDates(response.data);
  });
}

/**
 * @description Creates a new ABAC Application from the given ICreateApp data
 * @returns UseMutationResult<string, unknown, ICreateApp, unknown>
 * @queryInvalidations ["abacApps"]
 */
export function useCreateAbacAppMutation() {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, ICreateApp>(
    (createApp) => {
      return abacPostRequest(abacAppsControllerPath + "CreateApp", createApp);
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(["abacApps"]);
      },
      mutationKey: ["createAbacApp"],
    }
  );
}

/**
 * @description Updates the data for an existing ABAC Application from the given IEditApp data
 * @returns UseMutationResult<string, unknown, IEditApp, unknown>
 * @queryInvalidations ["abacApps"], ["abacApp", { id: <IEditApp Id> }]
 */
export function useEditAbacAppMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, IEditApp>(
    (editApp) => {
      return abacPutRequest(abacAppsControllerPath + "UpdateApp", editApp);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacApp", { id: id }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacApps"],
        });
        queryClient.invalidateQueries({
          queryKey: ["userFeatures"],
        });
      },
      mutationKey: ["editApp"],
    }
  );
}

// ABAC APP FEATURES

/**
 * @description Gets all Features for an ABAC Application with the given ID
 * @param appId The App ID for the App you wish to retrieve Features for
 * @returns UseQueryResult<IFeature[]>
 */
export function useGetAllFeaturesForAbacAppQuery(appId: string) {
  return useQuery<IFeature[]>(["abacFeaturesForApp", { id: appId }], async () => {
    const response: AxiosResponse<IFeature[]> = await abacGetRequest(
      abacFeaturesControllerPath + "GetAllFeaturesForApp/" + encodeURIComponent(appId)
    );

    return response.data.map(convertDates);
  });
}

/**
 * Fetches and returns the user's authorized features from the ABAC system.
 *
 * @returns UseQueryResult<string[]>
 *
 * @example
 * ```ts
 * const { data: userAuthorizedFeatures } = useGetUserFeatures();
 * const isUserAuthorized = userAuthorizedFeatures?.some((f) => f.includes(featureName));
 * ```
 */
export function useGetUserFeatures() {
  const { email } = useAppSelector(selectUserInformation);
  const { id: clientId } = useAppSelector(selectClientValue);
  return useQuery(
    ["userFeatures", { clientId, email }],
    async () => {
      const response: AxiosResponse<string[]> = await abacGetRequest(
        `${abacPermissionControllerPath}GetUserFeatures/${AppKey}/${encodeURIComponent(clientId)}/${encodeURIComponent(email)}`
      );
      return response.data;
    },
    {
      enabled: !!clientId && !!email,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );
}

/**
 * @description Gets a Feature by ID
 * @param featureId The ID for the Feature you wish to retrieve
 * @returns UseQueryResult<IFeature>
 */
export function useGetFeatureQuery(featureId: string) {
  return useQuery<IFeature>(["abacFeature", { id: featureId }], async () => {
    const response: AxiosResponse<IFeature> = await abacGetRequest(
      abacFeaturesControllerPath + "GetFeature/" + encodeURIComponent(featureId)
    );

    return convertDates(response.data);
  });
}

/**
 * @description Gets a Feature by ID with variation data
 * @param featureId The ID for the Feature you wish to retrieve
 * @returns UseQueryResult<IFeatureDto>
 */
export function useGetFeatureVariantsQuery(featureId: string, enabled: boolean = true) {
  return useQuery<IFeatureDto>(
    ["abacFeatureVariants", { id: featureId }],
    async () => {
      const response: AxiosResponse<IFeatureDto> = await abacGetRequest(
        abacFeaturesControllerPath + "GetFeatureVariants/" + encodeURIComponent(featureId)
      );

      return response.data;
    },
    {
      enabled: enabled && !!featureId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );
}

/**
 * @description Gets a all Features with variation data
 * @returns UseQueryResult<IFeatureDto[]>
 */
export function useGetAllFeatureVariantsQuery(enabled: boolean = true) {
  return useQuery<IFeatureDto[]>(
    ["abacFeatureVariants"],
    async () => {
      const response: AxiosResponse<IFeatureDto[]> = await abacGetRequest(
        abacFeaturesControllerPath + "GetAllFeatureVariants"
      );

      return response.data;
    },
    {
      enabled: enabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );
}

/**
 * @description Creates a new Feature for a specific Application from the given ICreateFeature data
 * @returns UseMutationResult<string, unknown, ICreateFeature, unknown>
 * @queryInvalidations ["abacFeaturesForApp", { id: appId }]
 */
export function useCreateAbacAppFeatureMutation(appId: string) {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, ICreateFeature>(
    (createFeature) => {
      return abacPostRequest(abacFeaturesControllerPath + "CreateFeature", createFeature);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacFeaturesForApp", { id: appId }],
        });
      },
      mutationKey: ["createAbacFeatureForApp", { id: appId }],
    }
  );
}

/**
 * @description Updates the data for an existing ABAC feature from the given IEditFeature data
 * @returns UseMutationResult<string, unknown, IEditFeature, unknown>
 * @queryInvalidations ["abacFeatures"], ["abacFeatures", { id: <IEditFeature Id> }]
 */
export function useEditAbacFeatureMutation(featureId: string, appId: string) {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, IEditFeature>(
    (editFeature) => {
      return abacPutRequest(abacFeaturesControllerPath + "UpdateFeature", editFeature);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacFeature", { id: featureId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacFeaturesForApp", { id: appId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacFeatureAttributesForFeature", { id: featureId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["userFeatures"],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacFeatureVariants"],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacClientFeatures"],
        });
      },
      mutationKey: ["editFeature"],
    }
  );
}

// ABAC APP ATTRIBUTES

/**
 * @description Gets an Attribute by ID
 * @param attributeId The ID for the Attribute you wish to retrieve
 * @returns UseQueryResult<IAttribute>
 */
export function useGetAttributeQuery(attributeId: string) {
  return useQuery<IAttribute>(["abacAttribute", { id: attributeId }], async () => {
    const response: AxiosResponse<IAttribute> = await abacGetRequest(
      abacAttributesControllerPath + "GetAttribute/" + encodeURIComponent(attributeId)
    );

    return convertDates(response.data);
  });
}

/**
 * @description Gets all Attributes for an ABAC Application with the given ID
 * @param appId The App ID for the App you wish to retrieve Attributes for
 * @returns UseQueryResult<IAttribute[]>
 */
export function useGetAllAttributesForAbacAppQuery(appId: string) {
  return useQuery<IAttribute[]>(
    ["abacAttributesForApp", { id: appId }],
    async () => {
      const response: AxiosResponse<IAttribute[]> = await abacGetRequest(
        abacAttributesControllerPath + "GetAllAttributesForApp/" + encodeURIComponent(appId)
      );
      return response.data.map(convertDates);
    },
    {
      enabled: !!appId,
      refetchOnWindowFocus: false,
    }
  );
}

/**
 * @description Creates a new Attribute for a specific Application from the given ICreateAttribute data
 * @returns UseMutationResult<string, unknown, ICreateAttribute, unknown>
 * @queryInvalidations ["abacAttributesForApp", { id: appId }], ["abacFeatureAttributesForFeature", { id: featureId }]
 */
export function useCreateAbacAppAttributeMutation(appId: string, featureId?: string) {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, ICreateAttribute>(
    (createAttribute) => {
      return abacPostRequest(abacAttributesControllerPath + "CreateAttribute", createAttribute);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacAttributesForApp", { id: appId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacFeatureAttributesForFeature", { id: featureId }],
        });
      },
      mutationKey: ["createAbacAttributeForApp", { id: appId }],
    }
  );
}

/**
 * @description Updates the data for an existing ABAC Attribute from the given IEditAttribute data
 * @returns UseMutationResult<string, unknown, IEditAttribute, unknown>
 * @queryInvalidations ["abacAttribute", { id: id }], ["abacAttributesForApp", { id: appId }]
 */
export function useEditAbacAttributeMutation(id: string, appId: string) {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, IEditAttribute>(
    (editAttribute) => {
      return abacPutRequest(abacAttributesControllerPath + "UpdateAttribute", editAttribute);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacAttribute", { id: id }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacAttributesForApp", { id: appId }],
        });
      },
      mutationKey: ["editAttribute"],
    }
  );
}

// ABAC APP FEATURE ATTRIBUTES

/**
 * @description Gets all FeatureAttributes assigned to a ABAC Feature with the given Feature ID
 * @param featureId The Feature ID for the Feature you wish to retrieve FeatureAttributes for
 * @returns UseQueryResult<IFeatureAttribute[]>
 */
export function useGetAllFeatureAttributesForAbacFeatureQuery(featureId: string) {
  return useQuery<IFeatureAttribute[]>(
    ["abacFeatureAttributesForFeature", { id: featureId }],
    async () => {
      const response: AxiosResponse<IFeatureAttribute[]> = await abacGetRequest(
        abacFeatureAttributesControllerPath + "GetAllFeatureAttributesForFeature/" + encodeURIComponent(featureId)
      );

      return response.data.map(convertDates);
    },
    { refetchOnWindowFocus: false }
  );
}

/**
 * @description Gets all FeatureAttributes assigned to an ABAC Attribute with the given Attribute ID
 * @param attributeId The Attribute ID for the Attribute you wish to retrieve FeatureAttributes for
 * @returns UseQueryResult<IFeatureAttribute[]>
 */
export function useGetAllFeatureAttributesForAbacAttributesQuery(attributeId: string) {
  return useQuery<IFeatureAttribute[]>(
    ["abacFeatureAttributesForAttribute", { id: attributeId }],
    async () => {
      const response: AxiosResponse<IFeatureAttribute[]> = await abacGetRequest(
        abacFeatureAttributesControllerPath + "GetAllFeatureAttributesForAttribute/" + encodeURIComponent(attributeId)
      );

      return response.data.map(convertDates);
    },
    { refetchOnWindowFocus: false }
  );
}

// ABAC USERCLIENTATTRIBUTES

/**
 * @description Gets all UserClientAttributes for a User by Client
 * @returns UseQueryResult<IUserClientAttribute[]>
 */
export function useGetUserClientAttributesByClientQuery(userId: string, baseClientId: string) {
  return useQuery<IUserClientAttribute[]>(
    ["abacUserClientAttributesByClient", { userId: userId }],
    async () => {
      const response: AxiosResponse<IUserClientAttribute[]> = await abacGetRequest(
        abacUserClientAttributeControllerPath +
          "GetUserClientAttributesByClient/" +
          encodeURIComponent(userId) +
          "/" +
          encodeURIComponent(baseClientId)
      );

      return response.data.map(convertDates);
    },
    { refetchOnWindowFocus: false }
  );
}

/**
 * @description Gets all UserClientAttributes for a User
 * @returns UseQueryResult<IUserClientAttribute[]>
 */
export function useGetAllUserClientAttributesForUserQuery<T>(
  userId: string,
  select?: (data: IUserClientAttribute[]) => T | undefined
) {
  return useQuery<IUserClientAttribute[], unknown, T | undefined>(
    ["abacAllUserClientAttributes", { userId: userId }],
    async () => {
      const response: AxiosResponse<IUserClientAttribute[]> = await abacGetRequest(
        abacUserClientAttributeControllerPath + "GetAllUserClientAttributes/" + encodeURIComponent(userId)
      );

      return response.data.map(convertDates);
    },
    { select, refetchOnWindowFocus: false }
  );
}

/**
 * @description Custom hook that gets all assigned ClientIds for a User based on the user's current
 * UserClientAttributes
 * @returns UseQueryResult<string[]>
 */
export const useGetAssignedAttributeClientIds = (userId: string) => {
  return useGetAllUserClientAttributesForUserQuery(userId, (userClientAttributes) => {
    return userClientAttributes
      .filter(
        (uca, index) => userClientAttributes.findIndex((a) => a.baseClientId === uca.baseClientId) === index
      )
      .map((uca) => uca.baseClientId);
  });
};

/**
 * @description Creates a new UserClientAttribute from the given ICreateUserClientAttribute data
 * @returns UseMutationResult<string, unknown, ICreateUserClientAttribute, unknown>
 * @queryInvalidations ["abacUserClientAttributesByClient", { userId: userId }],
 * ["abacAllUserClientAttributes", { userId: userId }]
 */
export function useCreateUserClientAttributeQuery(userId: string, clientId?: string) {
  const queryClient = useQueryClient();
  const { id: baseClientId } = useAppSelector(selectClientValue);

  return useMutation<string, unknown, ICreateUserClientAttribute>(
    (createUCA) => {
      return abacPostRequest(abacUserClientAttributeControllerPath + "CreateUserClientAttribute", createUCA);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacUserClientAttributesByClient", { userId: userId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacAllUserClientAttributes", { userId: userId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacUsersForClient", { id: clientId ?? baseClientId }],
        });
      },
      mutationKey: ["createUserClientAttribute"],
    }
  );
}

/**
 * @description Soft Deletes a UserClientAttribute entry by AttributeId
 * @returns UseMutationResult<string, unknown, string>
 * @queryInvalidations ["abacUserClientAttributesByClient", { userId: userId, baseClientId: baseClientId, appId: appId }],
 * ["abacAllUserClientAttributes", { userId: userId }]
 */
export function useDeleteUserClientAttributeQuery(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<string, unknown, string>(
    (ucaId) => {
      return abacDeleteRequest(abacUserClientAttributeControllerPath + "DeleteUserClientAttribute/" + encodeURIComponent(ucaId));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacUserClientAttributesByClient", { userId: userId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacAllUserClientAttributes", { userId: userId }],
        });
      },
      mutationKey: ["deleteUserClientAttribute"],
    }
  );
}

// ABAC USERS

/**
 * @description Gets all Users for a Base Client
 * @returns UseQueryResult<IUser[]>
 */
export function useGetAllUsersQuery() {
  return useQuery<IAbacUser[]>(["abacUsers"], async () => {
    const response: AxiosResponse<IAbacUser[]> = await abacGetRequest(abacUserControllerPath + "GetAllUsers");

    return response.data.map(convertDates);
  });
}

/**
 * @description Gets all Users for a Base Client
 * @returns UseQueryResult<IUser[]>
 */
export function useGetUsersForClientQuery() {
  const { id: clientId } = useAppSelector(selectClientValue);
  return useQuery<IAbacUser[]>(["abacUsersForClient", { id: clientId }], async () => {
    const response: AxiosResponse<IAbacUser[]> = await abacGetRequest(
      abacUserControllerPath + "GetAllUsersForClient/" + encodeURIComponent(clientId)
    );

    return response.data.map(convertDates);
  });
}

/**
 * @description Gets a User for by Id
 * @returns UseQueryResult<IUser>
 */
export function useGetUserQuery(userId: string) {
  return useQuery<IAbacUser>(["abacUser", { id: userId }], async () => {
    const response: AxiosResponse<IAbacUser> = await abacGetRequest(
      abacUserControllerPath + "GetUser/" + encodeURIComponent(userId)
    );

    return convertDates(response.data);
  });
}

/**
 * @description Creates a new Abac User from the given ICreateAbacUser data
 * @returns UseMutationResult<string, unknown, ICreateAbacUser>
 */
export function useCreateAbacUserMutation() {
  const queryClient = useQueryClient();
  const { id: clientId } = useAppSelector(selectClientValue);
  const { displayError } = useNotification();

  return useMutation<any, AxiosError, ICreateAbacUser>(
    (createUser) => {
      return abacPostRequest(abacUserControllerPath + "CreateUser", createUser);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacUsersForClient", { id: clientId }],
        });
      },
      onError: (error) => {
        if (error.response?.status === 400) {
          displayError(error.response?.data as string);
        } else {
          displayError("Error creating user!");
        }
      },
      mutationKey: ["createAbacUser"],
    }
  );
}

/**
 * @description Updates the data for an existing ABAC User from the given IEditAbacUser data
 * @returns UseMutationResult<string, unknown, IEditAbacUser, unknown>
 * @queryInvalidations ["abacUser", { id: userId }], ["abacUsersForClient", { id: clientId }]
 */
export function useEditAbacUserMutation(userId: string) {
  const queryClient = useQueryClient();
  const { id: clientId } = useAppSelector(selectClientValue);
  const { displayError } = useNotification();

  return useMutation<string, AxiosError, IEditAbacUser>(
    (editUser) => {
      return abacPutRequest(abacUserControllerPath + "UpdateUser", editUser);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["abacUsersForClient", { id: clientId }],
        });
        queryClient.invalidateQueries({
          queryKey: ["abacUser", { id: userId }],
        });
      },
      onError: (error) => {
        if (error.response?.status === 400) {
          displayError(error.response?.data as string);
        } else {
          displayError("Error updating user information!");
        }
      },
      mutationKey: ["editUser"],
    }
  );
}

/**
 * @description Sends a reset email request for the given user email to Auth0
 * @returns UseMutationResult<string, unknown, any>
 */
export function useResetUserPasswordMutation() {
  return useMutation<string, unknown, string>(
    (userEmail) => {
      return abacPostRequest(abacUserControllerPath + "ResetUserPassword/" + encodeURIComponent(userEmail));
    },
    {
      mutationKey: ["resetUserPassword"],
    }
  );
}

// ABAC CLIENTS

/**
 * @description Gets a list of all Client Features for a given Client
 * @returns UseQueryResult<IFeatureClientAttributeDto[]>
 */
export function useGetClientFeatures(clientId: string) {
  return useQuery<IClientFeaturesDto[]>(["abacClientFeatures", { id: clientId }], async () => {
    const response: AxiosResponse<IClientFeaturesDto[]> = await abacGetRequest(
      abacFeatureClientAttributeControllerPath + "GetAllFeatureClientAttributes?baseClientId=" + encodeURIComponent(clientId)
    );

    return response.data;
  });
}

/**
 * @description Updates the client's feature attributes
 * @returns UseMutationResult<boolean, unknown, ISyncClientFeature, unknown>
 */
export function useSyncFeatureClientAttributes() {
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, ISyncClientFeature>(
    (syncClientFeature) => {
      return abacPostRequest(
        abacFeatureClientAttributeControllerPath + "SyncFeatureClientAttributes",
        syncClientFeature
      );
    },
    {
      mutationKey: ["syncFeatureClientAttributes"],
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["abacClientFeatures", { id: variables.baseClientId }],
        });
      },
    }
  );
}

// HELPERS

/**
 * Converts dates from JSON string to Date objects
 *
 * @param app IApp with dates in string format
 * @returns IApp with date objects
 */
function convertDates(objectWithDates: any): any {
  return {
    ...objectWithDates,
    createdDate: new Date(objectWithDates.createdDate),
    updatedDate: new Date(objectWithDates.updatedDate),
  };
}
